import React, { useEffect, useState } from "react";
import { abrirVisor } from "@/lib/visor";
import { X, PawPrint, Link2, Eye, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { matchPets, computePetUpdates } from "@/lib/petMatch";
import { PetUpdateProposal } from "@/components/PetUpdateProposal";

const reasonsText = (reasons) => {
  if (!reasons || reasons.length === 0) return "";
  if (reasons.length === 1) return reasons[0];
  return reasons.slice(0, -1).join(", ") + " y " + reasons[reasons.length - 1];
};

const CONF = {
  alta: { t: "Coincidencia muy fiable", c: "text-[#15803D] bg-[#DCFCE7]" },
  media: { t: "Coincidencia probable", c: "text-[#B45309] bg-[#FEF3C7]" },
  baja: { t: "Coincidencia por confirmar", c: "text-[#6E7A74] bg-[#F2EDE4]" },
};

// Modal: vincular documentos ya subidos a la ficha de una mascota (caso retroactivo).
export const VincularDocsMascota = ({ pet, onClose }) => {
  const { getDocumentosSugeridosMascota, vincularDocumentoMascota, actualizarMascotaDesdeDocumento, getDocumentoBlobUrl } = useData();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // { doc, match, data }
  const [opening, setOpening] = useState(null);
  const [linkingId, setLinkingId] = useState(null);
  const [proposal, setProposal] = useState(null); // { docId, changes }
  const [updBusy, setUpdBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const docs = await getDocumentosSugeridosMascota(pet.id);
        if (!active) return;
        const matched = docs
          .map((doc) => {
            const mr = matchPets(doc.analisis, [pet]);
            return { doc, match: mr.best, data: mr.data };
          })
          .filter((x) => x.match && x.match.reasons.length > 0)
          .sort((a, b) => b.match.score - a.match.score);
        setItems(matched);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron buscar documentos.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet.id]);

  const verDoc = async (doc) => {
    setOpening(doc.id);
    try {
      abrirVisor(doc);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento.");
    } finally {
      setOpening(null);
    }
  };

  const vincular = async (item) => {
    setLinkingId(item.doc.id);
    try {
      await vincularDocumentoMascota(item.doc.id, pet.id);
      toast.success(`Documento vinculado a ${pet.name}`);
      const changes = computePetUpdates(item.data, pet);
      setItems((prev) => prev.filter((x) => x.doc.id !== item.doc.id));
      if (changes.length > 0) {
        setProposal({ docId: item.doc.id, docTitle: item.doc.filename, changes });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo vincular.");
    } finally {
      setLinkingId(null);
    }
  };

  const descartar = (id) => setItems((prev) => prev.filter((x) => x.doc.id !== id));

  const applyUpdates = async (updates) => {
    setUpdBusy(true);
    try {
      await actualizarMascotaDesdeDocumento(pet.id, {
        doc_id: proposal.docId,
        updates,
        nota_historial: `Ficha actualizada desde «${proposal.docTitle || "documento"}».`,
      });
      toast.success(`Ficha de ${pet.name} actualizada`);
      setProposal(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar la ficha.");
    } finally {
      setUpdBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="vincular-docs-modal">
      <div className="absolute inset-0 bg-[#1C2421]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-[#FAF8F5] rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl aldia-fade-up max-h-[90vh] overflow-y-auto">
        <button
          data-testid="vincular-docs-close"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5]"
        >
          <X size={22} className="text-[#4A5550]" />
        </button>

        <div className="flex items-center gap-2 text-[#2E5645]">
          <Link2 size={22} />
          <h2 className="font-heading text-2xl font-bold text-[#1C2421]">Vincular documentos existentes</h2>
        </div>
        <p className="text-base text-[#6E7A74] mt-1 mb-4">
          Busco entre tus documentos ya subidos los que podrían corresponder a {pet.name}.
        </p>

        {proposal ? (
          <PetUpdateProposal
            petName={pet.name}
            changes={proposal.changes}
            busy={updBusy}
            onApply={applyUpdates}
            onSkip={() => setProposal(null)}
          />
        ) : loading ? (
          <div className="py-10 text-center" data-testid="vincular-docs-loading">
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
            <p className="text-base text-[#6E7A74] mt-4">Analizando tus documentos…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center" data-testid="vincular-docs-empty">
            <PawPrint size={28} className="text-[#9AA39D] mx-auto mb-2" />
            <p className="text-base text-[#6E7A74]">
              No he encontrado documentos sin vincular que coincidan claramente con {pet.name}.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="vincular-docs-list">
            {items.map((item) => {
              const { doc, match } = item;
              const tipo = (doc.analisis && doc.analisis.tipo_documento && doc.analisis.tipo_documento.valor) || doc.type || "Documento";
              const conf = CONF[match.confidence] || CONF.baja;
              return (
                <div
                  key={doc.id}
                  data-testid={`vincular-doc-${doc.id}`}
                  className="bg-white border border-[#E5DFD5] rounded-2xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                      <FileText size={20} className="text-[#3A6B56]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-bold text-base text-[#1C2421] truncate">{tipo}</p>
                      <p className="text-sm text-[#6E7A74] truncate">{doc.filename} · {doc.date_label}</p>
                      <p className="text-sm text-[#2E5645] mt-1">
                        Coincidencias: <span className="font-semibold">{reasonsText(match.reasons)}</span>
                      </p>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 mt-1.5 ${conf.c}`}>
                        {conf.t}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      data-testid={`vincular-doc-link-${doc.id}`}
                      onClick={() => vincular(item)}
                      disabled={linkingId === doc.id}
                      className="flex-[1.4] min-h-[44px] rounded-xl bg-[#3A6B56] text-white font-semibold text-sm hover:bg-[#2E5645] disabled:opacity-60 flex items-center justify-center gap-1.5"
                    >
                      {linkingId === doc.id ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} Vincular
                    </button>
                    <button
                      data-testid={`vincular-doc-view-${doc.id}`}
                      onClick={() => verDoc(doc)}
                      disabled={opening === doc.id}
                      className="flex-1 min-h-[44px] rounded-xl bg-[#F2EDE4] text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#E5DFD5] disabled:opacity-60 flex items-center justify-center gap-1.5"
                    >
                      {opening === doc.id ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} Ver
                    </button>
                    <button
                      data-testid={`vincular-doc-dismiss-${doc.id}`}
                      onClick={() => descartar(doc.id)}
                      className="flex-1 min-h-[44px] rounded-xl bg-white text-[#6E7A74] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]"
                    >
                      Descartar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
